import { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { AuthContext } from '../../contexts/AuthContext';
import salesOrderService from '../../services/salesOrderService';
import receiptService from '../../services/receiptService';
import './SalesOrders.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const SalesOrders = () => {
  const { user } = useContext(AuthContext);
  const subsidiary = localStorage.getItem('selectedSubsidiaryKey') || 'LH';

  // Permission checks
  const checkPermission = (permissionName) => {
    if (!user?.permissions || !Array.isArray(user.permissions)) return false;

    return user.permissions.some(p => {
      // Handle permission objects with 'name' property
      if (typeof p === 'object' && p.name) {
        return p.name === permissionName;
      }
      // Handle plain string permissions
      if (typeof p === 'string') {
        return p === permissionName;
      }
      return false;
    });
  };

  const hasCreatePermission = checkPermission('salesorders.create');
  const hasUpdatePermission = checkPermission('salesorders.update');
  const hasDeletePermission = checkPermission('salesorders.delete');
  const hasReadPermission = checkPermission('salesorders.read');
  const hasPayPermission = checkPermission('salesorders.pay');
  const hasPayAdminPermission = checkPermission('salesorders.payadmin');
  const hasAssignPermission = checkPermission('salesorders.assign');
  const hasCancelPermission = checkPermission('salesorders.cancel');

  const [orders, setOrders] = useState([]);
  const [salesChannels, setSalesChannels] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    sales_channel_id: '',
    order_state: '',
    order_date: '',
    subsidiary: subsidiary,
    nvd: 'N',
    nvm: 'N',
    up: 'N',
    ndf: 'N'
  });

  const [showModal, setShowModal] = useState(false);
  const [editingOrder, setEditingOrder] = useState(null);
  const [isReadOnly, setIsReadOnly] = useState(false);

  const [orderForm, setOrderForm] = useState({
    order_date: new Date().toISOString().split('T')[0],
    sales_channel_id: '',
    order_state: 'Fulfilled',
    customer_id: '',
    customer_name: '',
    popup_yn: 'N',
    subsidiary: subsidiary,
    delivery_date: '',
    delivery_window: ''
  });

  const [orderLines, setOrderLines] = useState([{
    product_id: '',
    qty: '',
    unit_price: '',
    total_price: '',
    isDirty: false
  }]);

  const [showCustomerLov, setShowCustomerLov] = useState(true);
  const [customerInput, setCustomerInput] = useState('');
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);

  const [productInputs, setProductInputs] = useState({});
  const [filteredProducts, setFilteredProducts] = useState({});
  const [showProductDropdown, setShowProductDropdown] = useState({});

  const [packDetailLine, setPackDetailLine] = useState(null);

  // Receipt modal state
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [selectedOrderForReceipt, setSelectedOrderForReceipt] = useState(null);
  const [isReceiptReadOnly, setIsReceiptReadOnly] = useState(false);
  const [receiptLines, setReceiptLines] = useState([]);
  const [receiptNames, setReceiptNames] = useState([]);
  const [orderPaidTotal, setOrderPaidTotal] = useState({ total_amount: 0 });
  const [orderDeductions, setOrderDeductions] = useState([]);
  const [showFullyPaid, setShowFullyPaid] = useState(false);

  const [receiptForm, setReceiptForm] = useState({
    receipt_id: '',
    receipt_date: '',
    receipt_amount: '',
    balance_amount: '',
    receipt_type_id: '1'
  });

  const [deductionForm, setDeductionForm] = useState({
    deduction_type: '',
    deduction_amount: ''
  });

  const [commissionPercent, setCommissionPercent] = useState(15);
  const [tcsPercent, setTcsPercent] = useState(0.1);
  const [gstPercent, setGstPercent] = useState(5);

  // Cancel / refund modal state
  const [cancelOrder, setCancelOrder] = useState(null);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [cancelError, setCancelError] = useState('');

  // Request payment state
  const [requestingPayment, setRequestingPayment] = useState(new Set());

  const handleRequestPayment = async (order) => {
    setRequestingPayment(prev => new Set(prev).add(order.id));
    try {
      await salesOrderService.updateLcState(order.id, 'Payment Requested');
      setOrders(prev => prev.map(o =>
        o.id === order.id ? { ...o, order_lc_state: 'Payment Requested' } : o
      ));
      setSuccessMessage(`Payment requested for order ${order.order_no}.`);
    } catch (err) {
      setError(err.message || 'Failed to request payment');
    } finally {
      setRequestingPayment(prev => { const s = new Set(prev); s.delete(order.id); return s; });
    }
  };

  const handleCancelConfirm = async () => {
    setCancelLoading(true);
    setCancelError('');
    try {
      await axios.post(
        `${API_URL}/pay/refund`,
        { order_id: cancelOrder.id },
        { headers: { Authorization: `Bearer ${user?.token}` } }
      );
      setCancelOrder(null);
      fetchData();
      setSuccessMessage('Order cancelled and refund initiated successfully.');
      setTimeout(() => setSuccessMessage(''), 5000);
    } catch (err) {
      setCancelError(err.response?.data?.error || 'Refund failed. Please try again.');
    } finally {
      setCancelLoading(false);
    }
  };

  // Assignment modal state
  const [showAssignmentModal, setShowAssignmentModal] = useState(false);
  const [selectedOrderForAssignment, setSelectedOrderForAssignment] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [users, setUsers] = useState([]);
  const [assignmentForm, setAssignmentForm] = useState({
    user_id: '',
    order_role: ''
  });

  const orderStates = ['Draft', 'Dispatched', 'Entered', 'Fulfilled', 'Forecast', 'Paid'];

  useEffect(() => {
    fetchData();
  }, [filters, searchTerm]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [ordersRes, channelsRes, productsRes] = await Promise.all([
        salesOrderService.getAllOrders({
          ...filters,
          search: searchTerm  // Server-side search with OR logic (order_no OR customer_name)
        }),
        salesOrderService.getSalesChannels(),
        salesOrderService.getProducts(subsidiary)
      ]);

      if (ordersRes.success) {
        setOrders(Array.isArray(ordersRes.data) ? ordersRes.data : []);
      }
      if (channelsRes.success) {
        setSalesChannels(Array.isArray(channelsRes.data) ? channelsRes.data : []);
      }
      if (productsRes.success) {
        setProducts(Array.isArray(productsRes.data) ? productsRes.data : []);
      }
      setError('');
    } catch (err) {
      setError(err.message || 'Failed to fetch data');
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const handleCheckboxChange = (name) => {
    setFilters(prev => ({
      ...prev,
      [name]: prev[name] === 'N' ? 'Y' : 'N'
    }));
  };

  const resetFilters = () => {
    setSearchTerm('');
    setFilters({
      sales_channel_id: '',
      order_state: '',
      order_date: '',
      subsidiary: subsidiary,
      nvd: 'N',
      nvm: 'N',
      up: 'N',
      ndf: 'N'
    });
  };

  const handleOrderFormChange = async (e) => {
    const { name, value, type, checked } = e.target;

    if (name === 'sales_channel_id') {
      // Check customer LOV flag
      try {
        const response = await salesOrderService.checkCustomerLovFlag(value);
        if (response.success && response.data[0]?.customer_lov === 'Y') {
          setShowCustomerLov(true);
          const customersRes = await salesOrderService.getCustomers();
          if (customersRes.success) {
            setCustomers(Array.isArray(customersRes.data) ? customersRes.data : []);
          }
        } else {
          setShowCustomerLov(false);
          setOrderForm(prev => ({ ...prev, customer_id: '' }));
        }
      } catch (err) {
        console.error('Error checking customer LOV flag:', err);
      }
    }

    setOrderForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (checked ? 'Y' : 'N') : value
    }));
  };

  const handleCustomerInputChange = (e) => {
    const value = e.target.value;
    setCustomerInput(value);

    if (showCustomerLov) {
      if (value.trim() === '') {
        setFilteredCustomers([]);
        setShowCustomerDropdown(false);
        setOrderForm(prev => ({ ...prev, customer_id: '', customer_name: '' }));
      } else {
        const filtered = customers.filter(customer =>
          customer.customer_name.toLowerCase().includes(value.toLowerCase())
        );
        setFilteredCustomers(filtered);
        setShowCustomerDropdown(true);
      }
    } else {
      setOrderForm(prev => ({ ...prev, customer_name: value }));
    }
  };

  const handleCustomerSelect = (customer) => {
    setCustomerInput(customer.customer_name);
    setOrderForm(prev => ({ ...prev, customer_id: customer.id, customer_name: customer.customer_name }));
    setShowCustomerDropdown(false);
    setFilteredCustomers([]);
  };

  const handleProductInputChange = (index, value) => {
    // Update the input value for this line
    setProductInputs(prev => ({ ...prev, [index]: value }));

    if (value.trim() === '') {
      setFilteredProducts(prev => ({ ...prev, [index]: [] }));
      setShowProductDropdown(prev => ({ ...prev, [index]: false }));

      // Clear product selection
      const newLines = [...orderLines];
      newLines[index].product_id = '';
      newLines[index].isDirty = true;
      setOrderLines(newLines);
    } else {
      // Filter products
      const filtered = products.filter(product =>
        product.name.toLowerCase().includes(value.toLowerCase())
      );
      setFilteredProducts(prev => ({ ...prev, [index]: filtered }));
      setShowProductDropdown(prev => ({ ...prev, [index]: true }));
    }
  };

  const handleProductSelect = (index, product) => {
    // Update input display
    setProductInputs(prev => ({ ...prev, [index]: product.name }));

    // Update order line
    const newLines = [...orderLines];
    newLines[index].product_id = product.id;
    newLines[index].isDirty = true;
    setOrderLines(newLines);

    // Hide dropdown
    setShowProductDropdown(prev => ({ ...prev, [index]: false }));
    setFilteredProducts(prev => ({ ...prev, [index]: [] }));
  };

  const handleLineChange = (index, field, value) => {
    const newLines = [...orderLines];
    newLines[index][field] = value;

    // Mark line as dirty when any field is changed
    newLines[index].isDirty = true;

    // Auto-calculate total price
    if (field === 'qty' || field === 'unit_price') {
      const qty = parseFloat(newLines[index].qty) || 0;
      const unitPrice = parseFloat(newLines[index].unit_price) || 0;
      newLines[index].total_price = (qty * unitPrice).toFixed(2);
    }

    setOrderLines(newLines);
  };

  const addOrderLine = () => {
    const newIndex = orderLines.length;
    setOrderLines([...orderLines, {
      product_id: '',
      qty: '',
      unit_price: '',
      total_price: '',
      isDirty: false
    }]);
    // Initialize empty product input for new line
    setProductInputs(prev => ({ ...prev, [newIndex]: '' }));
  };

  const removeOrderLine = (index) => {
    const newLines = orderLines.filter((_, i) => i !== index);
    setOrderLines(newLines.length > 0 ? newLines : [{
      product_id: '',
      qty: '',
      unit_price: '',
      total_price: '',
      isDirty: false
    }]);

    // Clean up product input state for removed line
    setProductInputs(prev => {
      const newInputs = { ...prev };
      delete newInputs[index];
      // Re-index remaining inputs
      const reindexed = {};
      Object.keys(newInputs).forEach(key => {
        const oldIndex = parseInt(key);
        if (oldIndex > index) {
          reindexed[oldIndex - 1] = newInputs[key];
        } else {
          reindexed[key] = newInputs[key];
        }
      });
      return reindexed;
    });

    // Clean up dropdown states
    setFilteredProducts(prev => {
      const newFiltered = { ...prev };
      delete newFiltered[index];
      return newFiltered;
    });
    setShowProductDropdown(prev => {
      const newShow = { ...prev };
      delete newShow[index];
      return newShow;
    });
  };

  const handleCreateNew = () => {
    setEditingOrder(null);
    setIsReadOnly(false); // Always allow editing when creating new
    setOrderForm({
      order_date: new Date().toISOString().split('T')[0],
      sales_channel_id: '',
      order_state: 'Fulfilled',
      customer_id: '',
      customer_name: '',
      popup_yn: 'N',
      subsidiary: subsidiary,
      delivery_date: '',
      delivery_window: ''
    });
    setOrderLines([
      { product_id: '', qty: '', unit_price: '', total_price: '', isDirty: false },
      { product_id: '', qty: '', unit_price: '', total_price: '', isDirty: false },
      { product_id: '', qty: '', unit_price: '', total_price: '', isDirty: false },
      { product_id: '', qty: '', unit_price: '', total_price: '', isDirty: false },
      { product_id: '', qty: '', unit_price: '', total_price: '', isDirty: false }
    ]);
    setCustomerInput('');
    // Initialize empty product inputs for new order
    setProductInputs({ 0: '', 1: '', 2: '', 3: '', 4: '' });
    setFilteredProducts({});
    setShowProductDropdown({});
    setShowModal(true);
  };

  const handleEdit = async (order, readOnly = false) => {
    setEditingOrder(order);

    // Set read-only mode based on explicit parameter or permission/order state
    setIsReadOnly(readOnly);

    // Fix date conversion: convert UTC timestamp to local date
    let formattedDate = '';
    if (order.order_date) {
      // Create a Date object from the UTC timestamp
      const date = new Date(order.order_date);

      // Extract the local date components (this accounts for timezone conversion)
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');

      formattedDate = `${year}-${month}-${day}`;
    }

    let formattedDeliveryDate = '';
    if (order.delivery_date) {
      const dd = new Date(order.delivery_date);
      formattedDeliveryDate = `${dd.getFullYear()}-${String(dd.getMonth()+1).padStart(2,'0')}-${String(dd.getDate()).padStart(2,'0')}`;
    }

    setOrderForm({
      ...order,
      order_date: formattedDate,
      delivery_date: formattedDeliveryDate
    });

    setCustomerInput(order.customer_name || '');

    // Load customer LOV for the existing sales channel
    if (order.sales_channel_id) {
      try {
        const lovRes = await salesOrderService.checkCustomerLovFlag(order.sales_channel_id);
        if (lovRes.success && lovRes.data[0]?.customer_lov === 'Y') {
          setShowCustomerLov(true);
          const customersRes = await salesOrderService.getCustomers();
          if (customersRes.success) {
            setCustomers(Array.isArray(customersRes.data) ? customersRes.data : []);
          }
        } else {
          setShowCustomerLov(false);
        }
      } catch (err) {
        console.error('Error loading customer LOV on edit:', err);
      }
    }

    // Load order lines
    try {
      const linesRes = await salesOrderService.getOrderLines(order.id);
      if (linesRes.success && Array.isArray(linesRes.data) && linesRes.data.length > 0) {
        setOrderLines(linesRes.data.map(line => ({
          id: line.id,
          product_id: line.product_id,
          pack_id: line.pack_id,
          is_pack: line.is_pack,
          name: line.name,
          selectionGroups: line.selectionGroups || [],
          qty: line.qty,
          unit_price: line.unit_price,
          total_price: line.total_price,
          isDirty: false
        })));

        // Initialize product inputs for existing lines
        const inputs = {};
        linesRes.data.forEach((line, index) => {
          const product = products.find(p => p.id === line.product_id);
          inputs[index] = product ? product.name : '';
        });
        setProductInputs(inputs);
      } else {
        setOrderLines([{
          product_id: '',
          qty: '',
          unit_price: '',
          total_price: '',
          isDirty: false
        }]);
        setProductInputs({ 0: '' });
      }
    } catch (err) {
      console.error('Error loading order lines:', err);
    }

    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');

    // Validate customer selection
    // If the sales channel requires customer LOV, ensure a valid customer_id is selected
    if (showCustomerLov && !orderForm.customer_id) {
      setError('Please select a valid customer from the list');
      return;
    }

    // Different logic for CREATE vs UPDATE
    let linesToSend;

    if (editingOrder) {
      // UPDATE: Send ALL lines (both dirty and non-dirty) to preserve existing lines
      // Filter out empty lines (lines without product_id)
      linesToSend = orderLines.filter(line => !line.is_pack && line.product_id && line.qty && line.unit_price);

      // Allow empty lines array for UPDATE (user might want to delete all lines)
      // Backend will handle this and set order_amount to 0
    } else {
      // CREATE: Only send dirty (modified) lines
      const dirtyLines = orderLines.filter(line => line.isDirty);

      // Validate dirty lines - only dirty lines need complete data
      const invalidDirtyLines = dirtyLines.filter(line => !line.product_id || !line.qty || !line.unit_price);
      if (invalidDirtyLines.length > 0) {
        setError('Please complete all fields for modified order lines or remove incomplete lines');
        return;
      }

      // Valid lines are those that are dirty and have all required fields
      linesToSend = dirtyLines.filter(line => line.product_id && line.qty && line.unit_price);

      if (linesToSend.length === 0) {
        setError('Please add at least one order line with complete data');
        return;
      }
    }

    try {
      // Calculate order amount from lines to send
      const orderAmount = linesToSend.reduce((sum, line) => sum + parseFloat(line.total_price || 0), 0);

      // Clean up lines - remove isDirty flag before sending to backend
      const cleanedLines = linesToSend.map(line => {
        const { isDirty, ...cleanLine } = line;
        return { ...cleanLine, current_stock: parseFloat(cleanLine.qty) || 0 };
      });

      const orderData = {
        ...orderForm,
        order_amount: orderAmount,
        receipt_amount: orderAmount,
        lines: cleanedLines
      };

      let response;
      if (editingOrder) {
        response = await salesOrderService.updateOrder(editingOrder.id, orderData);
      } else {
        response = await salesOrderService.createOrder(orderData);
      }

      if (response.success) {
        setSuccessMessage(editingOrder ? 'Order updated successfully!' : 'Order created successfully!');
        setShowModal(false);
        setEditingOrder(null);
        resetForm();
        fetchData();
        setTimeout(() => setSuccessMessage(''), 5000);
      }
    } catch (err) {
      console.error('Save order error:', err);
      setError(err.message || 'Failed to save order');
    }
  };

  const handleStoreOrderDelete = async (order) => {
    if (!window.confirm(
      `Delete store partner order "${order.order_no}" and ALL related data?\n\nThis action cannot be undone.`
    )) return;
    try {
      const response = await salesOrderService.deleteStoreOrder(order.id);
      if (response.success) {
        setSuccessMessage(`Order ${order.order_no} and all related data deleted.`);
        fetchData();
        setTimeout(() => setSuccessMessage(''), 5000);
      }
    } catch (err) {
      setError(err.error || err.message || 'Failed to delete store partner order');
    }
  };

  const handleDelete = async (order) => {
    if (!window.confirm(`Are you sure you want to delete order "${order.order_no}"?`)) {
      return;
    }

    try {
      const response = await salesOrderService.deleteOrder(order.id);
      if (response.success) {
        setSuccessMessage('Order deleted successfully!');
        fetchData();
        setTimeout(() => setSuccessMessage(''), 5000);
      }
    } catch (err) {
      console.error('Delete order error:', err);
      setError(err.message || 'Failed to delete order');
    }
  };

  const resetForm = () => {
    setOrderForm({
      order_date: new Date().toISOString().split('T')[0],
      sales_channel_id: '',
      order_state: 'Fulfilled',
      customer_id: '',
      customer_name: '',
      popup_yn: 'N',
      subsidiary: subsidiary,
      delivery_date: '',
      delivery_window: ''
    });
    setOrderLines([
      { product_id: '', qty: '', unit_price: '', total_price: '', isDirty: false },
      { product_id: '', qty: '', unit_price: '', total_price: '', isDirty: false },
      { product_id: '', qty: '', unit_price: '', total_price: '', isDirty: false },
      { product_id: '', qty: '', unit_price: '', total_price: '', isDirty: false },
      { product_id: '', qty: '', unit_price: '', total_price: '', isDirty: false }
    ]);
    setCustomerInput('');
  };

  // Receipt Modal Functions
  const handleOpenReceiptModal = async (order) => {
    setSelectedOrderForReceipt(order);
    setShowReceiptModal(true);
    setError('');
    setSuccessMessage('');

    // Determine if receipt modal should be read-only
    // Read-only if: order is Paid AND user only has 'pay' permission (not 'payadmin')
    const shouldBeReadOnly = order.order_state === 'Paid' && hasPayPermission && !hasPayAdminPermission;
    setIsReceiptReadOnly(shouldBeReadOnly);

    // Fetch receipt-related data
    try {
      const [receiptLinesRes, receiptNamesRes, paidTotalRes, deductionsRes] = await Promise.all([
        receiptService.getAllReceiptLinesByOrderId(order.id),
        receiptService.getReceiptNames(),
        receiptService.getOrderPaidTotal(order.id),
        receiptService.getDeductionsByOrderId(order.id)
      ]);

      if (receiptLinesRes.success) {
        setReceiptLines(receiptLinesRes.data);
      }
      if (receiptNamesRes.success) {
        setReceiptNames(receiptNamesRes.data);
      }
      if (paidTotalRes.success) {
        setOrderPaidTotal(paidTotalRes.data);

        // Check if order is fully paid
        if (order.receipt_amount && paidTotalRes.data.total_amount &&
            Number(order.receipt_amount) === Number(paidTotalRes.data.total_amount)) {
          setShowFullyPaid(true);
        } else {
          setShowFullyPaid(false);
        }
      }
      if (deductionsRes.success) {
        setOrderDeductions(deductionsRes.data);
      }
    } catch (err) {
      console.error('Error fetching receipt data:', err);
      setError('Failed to load receipt data');
    }
  };

  const handleCloseReceiptModal = () => {
    setShowReceiptModal(false);
    setSelectedOrderForReceipt(null);
    setIsReceiptReadOnly(false);
    setReceiptForm({
      receipt_id: '',
      receipt_date: '',
      receipt_amount: '',
      balance_amount: '',
      receipt_type_id: '1'
    });
    setDeductionForm({
      deduction_type: '',
      deduction_amount: ''
    });
    setReceiptLines([]);
    setReceiptNames([]);
    setOrderPaidTotal({ total_amount: 0 });
    setOrderDeductions([]);
    setShowFullyPaid(false);
  };

  const handleReceiptFormChange = async (e) => {
    const { name, value } = e.target;
    setReceiptForm(prev => ({ ...prev, [name]: value }));

    // Fetch receipt details when receipt is selected
    if (name === 'receipt_id' && value) {
      try {
        const res = await receiptService.getReceiptDetailsById(value);
        if (res.success) {
          setReceiptForm(prev => ({
            ...prev,
            receipt_date: res.data.receipt_date ? new Date(res.data.receipt_date).toISOString().split('T')[0] : '',
            balance_amount: res.data.balance_amount || ''
          }));
        }
      } catch (err) {
        console.error('Error fetching receipt details:', err);
      }
    }
  };

  const handleReceiptSubmit = async (e) => {
    e.preventDefault();
    setError('');

    try {
      const receiptData = {
        ...receiptForm,
        order_id: selectedOrderForReceipt.id
      };

      await receiptService.createReceiptLine(receiptData);
      setSuccessMessage('Receipt line added successfully!');

      // Reset form and refresh data
      setReceiptForm({
        receipt_id: '',
        receipt_date: '',
        receipt_amount: '',
        balance_amount: '',
        receipt_type_id: '1'
      });

      // Refresh receipt lines and paid total
      const [receiptLinesRes, paidTotalRes] = await Promise.all([
        receiptService.getAllReceiptLinesByOrderId(selectedOrderForReceipt.id),
        receiptService.getOrderPaidTotal(selectedOrderForReceipt.id)
      ]);

      if (receiptLinesRes.success) {
        setReceiptLines(receiptLinesRes.data);
      }
      if (paidTotalRes.success) {
        setOrderPaidTotal(paidTotalRes.data);
      }

      // Refresh orders list to update receipt amounts
      fetchData();

      setTimeout(() => setSuccessMessage(''), 5000);
    } catch (err) {
      console.error('Error adding receipt:', err);
      setError(err.error || err.message || 'Failed to add receipt');
    }
  };

  const handleDeleteReceiptLine = async (receiptLine) => {
    if (!window.confirm('Are you sure you want to delete this receipt line?')) {
      return;
    }

    try {
      await receiptService.deleteReceiptLine(
        receiptLine.id,
        receiptLine.receipt_id,
        receiptLine.receipt_amount
      );

      setSuccessMessage('Receipt line deleted successfully!');

      // Refresh receipt lines and paid total
      const [receiptLinesRes, paidTotalRes] = await Promise.all([
        receiptService.getAllReceiptLinesByOrderId(selectedOrderForReceipt.id),
        receiptService.getOrderPaidTotal(selectedOrderForReceipt.id)
      ]);

      if (receiptLinesRes.success) {
        setReceiptLines(receiptLinesRes.data);
      }
      if (paidTotalRes.success) {
        setOrderPaidTotal(paidTotalRes.data);
      }

      // Refresh orders list
      fetchData();

      setTimeout(() => setSuccessMessage(''), 5000);
    } catch (err) {
      console.error('Error deleting receipt:', err);
      setError(err.error || err.message || 'Failed to delete receipt');
    }
  };

  const handleDeductionFormChange = (e) => {
    const { name, value } = e.target;
    setDeductionForm(prev => ({ ...prev, [name]: value }));
  };

  const handleDeductionSubmit = async (e) => {
    e.preventDefault();
    setError('');

    try {
      const deductionData = {
        ...deductionForm,
        order_id: selectedOrderForReceipt.id
      };

      await receiptService.createDeduction(deductionData);
      setSuccessMessage('Deduction added successfully!');

      // Reset form and refresh data
      setDeductionForm({
        deduction_type: '',
        deduction_amount: ''
      });

      // Refresh deductions
      const deductionsRes = await receiptService.getDeductionsByOrderId(selectedOrderForReceipt.id);
      if (deductionsRes.success) {
        setOrderDeductions(deductionsRes.data);
      }

      setTimeout(() => setSuccessMessage(''), 5000);
    } catch (err) {
      console.error('Error adding deduction:', err);
      setError(err.error || err.message || 'Failed to add deduction');
    }
  };

  const handleGenerateDeductions = async () => {
    setError('');

    try {
      const params = {
        order_id: selectedOrderForReceipt.id,
        order_amount: selectedOrderForReceipt.receipt_amount,
        commission_percent: commissionPercent,
        gst_percent: gstPercent,
        tcs_percent: tcsPercent
      };

      await receiptService.generateAutoDeductions(params);
      setSuccessMessage('Deductions generated successfully!');

      // Refresh deductions
      const deductionsRes = await receiptService.getDeductionsByOrderId(selectedOrderForReceipt.id);
      if (deductionsRes.success) {
        setOrderDeductions(deductionsRes.data);
      }

      setTimeout(() => setSuccessMessage(''), 5000);
    } catch (err) {
      console.error('Error generating deductions:', err);
      setError(err.error || err.message || 'Failed to generate deductions');
    }
  };

  const handleDeleteDeduction = async (deduction) => {
    if (!window.confirm('Are you sure you want to delete this deduction?')) {
      return;
    }

    try {
      await receiptService.deleteDeduction(deduction.id, deduction.expense_id);

      setSuccessMessage('Deduction deleted successfully!');

      // Refresh deductions
      const deductionsRes = await receiptService.getDeductionsByOrderId(selectedOrderForReceipt.id);
      if (deductionsRes.success) {
        setOrderDeductions(deductionsRes.data);
      }

      setTimeout(() => setSuccessMessage(''), 5000);
    } catch (err) {
      console.error('Error deleting deduction:', err);
      setError(err.error || err.message || 'Failed to delete deduction');
    }
  };

  const handleRefreshPaidTotal = async () => {
    try {
      const paidTotalRes = await receiptService.getOrderPaidTotal(selectedOrderForReceipt.id);
      if (paidTotalRes.success) {
        setOrderPaidTotal(paidTotalRes.data);

        // Check if order is fully paid
        if (selectedOrderForReceipt.receipt_amount && paidTotalRes.data.total_amount &&
            Number(selectedOrderForReceipt.receipt_amount) === Number(paidTotalRes.data.total_amount)) {
          setShowFullyPaid(true);
        } else {
          setShowFullyPaid(false);
        }
      }
    } catch (err) {
      console.error('Error refreshing paid total:', err);
    }
  };

  const handleMarkFullyPaid = async () => {
    try {
      const orderData = {
        order_state: 'Paid',
        amount_received: 'Y'
      };

      await salesOrderService.updateOrder(selectedOrderForReceipt.id, orderData);
      setSuccessMessage('Order marked as fully paid!');

      // Refresh orders list
      fetchData();

      // Update selected order state
      setSelectedOrderForReceipt(prev => ({
        ...prev,
        order_state: 'Paid',
        amount_received: 'Y'
      }));

      setTimeout(() => setSuccessMessage(''), 5000);
    } catch (err) {
      console.error('Error marking order as paid:', err);
      setError('Failed to mark order as paid');
    }
  };

  // Assignment Modal Functions
  const handleOpenAssignmentModal = async (order) => {
    setSelectedOrderForAssignment(order);
    setShowAssignmentModal(true);
    setError('');
    setSuccessMessage('');

    // Fetch assignment-related data
    try {
      const [assignmentsRes, usersRes] = await Promise.all([
        salesOrderService.getAssignmentsByOrderId(order.id),
        salesOrderService.getUsers()
      ]);

      if (assignmentsRes.success) {
        setAssignments(assignmentsRes.data);
      }
      if (usersRes.success) {
        setUsers(usersRes.data);
      }
    } catch (err) {
      console.error('Error fetching assignment data:', err);
      setError('Failed to load assignment data');
    }
  };

  const handleCloseAssignmentModal = () => {
    setShowAssignmentModal(false);
    setSelectedOrderForAssignment(null);
    setAssignmentForm({
      user_id: '',
      order_role: ''
    });
    setAssignments([]);
    setUsers([]);
  };

  const handleAssignmentFormChange = (e) => {
    const { name, value } = e.target;
    setAssignmentForm(prev => ({ ...prev, [name]: value }));
  };

  const handleAssignmentSubmit = async (e) => {
    e.preventDefault();
    setError('');

    try {
      const assignmentData = {
        ...assignmentForm,
        order_id: selectedOrderForAssignment.id
      };

      await salesOrderService.createAssignment(assignmentData);
      setSuccessMessage('Assignment added successfully!');

      // Reset form and refresh data
      setAssignmentForm({
        user_id: '',
        order_role: ''
      });

      // Refresh assignments
      const assignmentsRes = await salesOrderService.getAssignmentsByOrderId(selectedOrderForAssignment.id);
      if (assignmentsRes.success) {
        setAssignments(assignmentsRes.data);
      }

      setTimeout(() => setSuccessMessage(''), 5000);
    } catch (err) {
      console.error('Error adding assignment:', err);
      setError(err.error || err.message || 'Failed to add assignment');
    }
  };

  const handleDeleteAssignment = async (assignment) => {
    if (!window.confirm('Are you sure you want to delete this assignment?')) {
      return;
    }

    try {
      await salesOrderService.deleteAssignment(assignment.id);

      setSuccessMessage('Assignment deleted successfully!');

      // Refresh assignments
      const assignmentsRes = await salesOrderService.getAssignmentsByOrderId(selectedOrderForAssignment.id);
      if (assignmentsRes.success) {
        setAssignments(assignmentsRes.data);
      }

      setTimeout(() => setSuccessMessage(''), 5000);
    } catch (err) {
      console.error('Error deleting assignment:', err);
      setError(err.error || err.message || 'Failed to delete assignment');
    }
  };

  // Orders are now filtered server-side using the search parameter
  const filteredOrders = orders;

  return (
    <div className="sales-orders-management">
      <div className="section-header">
        <div>
          <h2>Sales Order Management</h2>
          <p>Manage sales orders and order lines</p>
        </div>
        {hasCreatePermission && (
          <button className="btn-create" onClick={handleCreateNew}>
            + Create Order
          </button>
        )}
      </div>

      {error && <div className="error-message">{error}</div>}
      {successMessage && <div className="success-message">{successMessage}</div>}

      {/* Search and Filters */}
      <div className="search-filter-container">
        <div className="search-container">
          <input
            type="text"
            className="search-input"
            placeholder="Search by order number or customer name..."
            value={searchTerm}
            onChange={handleSearchChange}
          />
          <span className="search-icon">🔍</span>
        </div>

        <div className="filters-row">
          <select
            name="sales_channel_id"
            className="filter-select"
            value={filters.sales_channel_id}
            onChange={handleFilterChange}
          >
            <option value="">All Sales Channels</option>
            {salesChannels.map(channel => (
              <option key={channel.id} value={channel.id}>{channel.name}</option>
            ))}
          </select>

          <select
            name="order_state"
            className="filter-select"
            value={filters.order_state}
            onChange={handleFilterChange}
          >
            <option value="">All States</option>
            {orderStates.map(state => (
              <option key={state} value={state}>{state}</option>
            ))}
          </select>

          <input
            type="date"
            name="order_date"
            className="filter-input"
            value={filters.order_date}
            onChange={handleFilterChange}
          />

          <label className="filter-checkbox">
            <input
              type="checkbox"
              checked={filters.nvd === 'Y'}
              onChange={() => handleCheckboxChange('nvd')}
            />
            <span>NVD</span>
          </label>

          <label className="filter-checkbox">
            <input
              type="checkbox"
              checked={filters.up === 'Y'}
              onChange={() => handleCheckboxChange('up')}
            />
            <span>UP</span>
          </label>

          <label className="filter-checkbox">
            <input
              type="checkbox"
              checked={filters.nvm === 'Y'}
              onChange={() => handleCheckboxChange('nvm')}
            />
            <span>NVM</span>
          </label>

          <label className="filter-checkbox">
            <input
              type="checkbox"
              checked={filters.ndf === 'Y'}
              onChange={() => handleCheckboxChange('ndf')}
            />
            <span>NDF</span>
          </label>

          <button className="btn-reset" onClick={resetFilters}>
            Reset
          </button>
        </div>
      </div>

      {loading ? (
        <div className="loading">Loading sales orders...</div>
      ) : (
        <>
          <div className="orders-table-container">
            <table className="orders-table">
              <thead>
                <tr>
                  <th>Order Number</th>
                  <th>Customer</th>
                  <th>Date</th>
                  <th>Amount</th>
                  <th>State</th>
                  <th>Channel</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredOrders.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="no-data">No sales orders found</td>
                  </tr>
                ) : (
                  filteredOrders.map(order => (
                    <tr key={order.id}>
                      <td><strong>{order.order_no}</strong></td>
                      <td>{order.customer_name || '-'}</td>
                      <td>{order.order_date ? new Date(order.order_date).toLocaleDateString() : '-'}</td>
                      <td>₹{order.order_amount || '0.00'}</td>
                      <td>
                        <span className={`state-badge state-${order.order_state?.toLowerCase()}`}>
                          {order.order_state}
                        </span>
                      </td>
                      <td>{order.sales_channel || '-'}</td>
                      <td>
                        <div className="action-buttons">
                          {/* Edit icon: For non-Paid orders with update permission OR Paid orders with payadmin permission */}
                          {((order.order_state !== 'Paid' && hasUpdatePermission) || (order.order_state === 'Paid' && hasPayAdminPermission)) && (
                            <button
                              className="btn-icon btn-edit"
                              onClick={() => handleEdit(order, false)}
                              title="Edit Order"
                            >
                              ✏️
                            </button>
                          )}

                          {/* View icon: Show when user has read permission and edit icon is not shown */}
                          {hasReadPermission && !((order.order_state !== 'Paid' && hasUpdatePermission) || (order.order_state === 'Paid' && hasPayAdminPermission)) && (
                            <button
                              className="btn-icon btn-view"
                              onClick={() => handleEdit(order, true)}
                              title="View Order Details"
                            >
                              👁️
                            </button>
                          )}

                          {/* Delete icon: For non-Paid orders with delete permission OR Paid orders with payadmin permission */}
                          {((order.order_state !== 'Paid' && hasDeletePermission) || (order.order_state === 'Paid' && hasPayAdminPermission)) && (
                            <button
                              className="btn-icon btn-delete"
                              onClick={() => handleDelete(order)}
                              title="Delete Order"
                            >
                              🗑️
                            </button>
                          )}
                          {(hasPayPermission || hasPayAdminPermission) && (
                            <button
                              className="btn-icon btn-receipt"
                              onClick={() => handleOpenReceiptModal(order)}
                              title="Manage Receipts & Deductions"
                            >
                              ₹
                            </button>
                          )}
                          {hasAssignPermission && (
                            <button
                              className="btn-icon btn-assign"
                              onClick={() => handleOpenAssignmentModal(order)}
                              title="Manage Assignments"
                            >
                              👤
                            </button>
                          )}
                          {hasUpdatePermission &&
                            parseInt(order.sales_channel_id) === 2 &&
                            order.order_state === 'Draft' &&
                            !order.online_payment_id &&
                            !(parseFloat(order.wallet_cash_amount || 0) > 0) &&
                            !(parseFloat(order.wallet_points_amount || 0) > 0) &&
                            !(parseFloat(order.razorpay_amount || 0) > 0) &&
                            order.order_lc_state !== 'Payment Requested' && (
                            <button
                              className="btn-icon btn-request-payment"
                              onClick={() => handleRequestPayment(order)}
                              disabled={requestingPayment.has(order.id)}
                              title="Request payment from customer"
                            >
                              {requestingPayment.has(order.id) ? '…' : '💳'}
                            </button>
                          )}
                          {hasCancelPermission && (order.online_payment_id || parseFloat(order.wallet_cash_amount || 0) > 0 || parseFloat(order.wallet_points_amount || 0) > 0) && order.order_state !== 'Cancelled' && (
                            <button
                              className="btn-icon btn-cancel-order"
                              onClick={() => { setCancelOrder(order); setCancelError(''); }}
                              title="Cancel order &amp; refund"
                            >
                              ✖
                            </button>
                          )}
                          {user?.role === 'SADM' &&
                            process.env.REACT_APP_API_URL !== 'https://prebook.thenewluxury.in/api' &&
                            (
                              [98, 100, 158, 159, 177].includes(parseInt(order.customer_id)) ||
                              (order.order_state === 'Draft' && !order.online_payment_id)
                            ) && (
                            <button
                              className="btn-icon btn-force-delete"
                              onClick={() => handleStoreOrderDelete(order)}
                              title="Force delete store partner order and all related data (SADM only)"
                            >
                              🗑️
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Create/Edit Order Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content order-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{isReadOnly ? 'View Order Details' : (editingOrder ? 'Edit Order' : 'Create New Order')}</h3>
              <button className="close-button" onClick={() => { setShowModal(false); setEditingOrder(null); setIsReadOnly(false); resetForm(); }}>×</button>
            </div>

            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', height: 'calc(100% - 60px)' }}>
              {error && <div className="error-message" style={{ margin: '0 20px 10px 20px' }}>{error}</div>}

              <div className="modal-body">
                {/* Order Header Section */}
                <div className="form-section">
                  <div className="form-grid">
                    <div className="form-group">
                      <label>Sales Channel *</label>
                      <select
                        name="sales_channel_id"
                        value={orderForm.sales_channel_id}
                        onChange={handleOrderFormChange}
                        required
                        disabled={isReadOnly}
                      >
                        <option value="">Select Channel</option>
                        {salesChannels.map(channel => (
                          <option key={channel.id} value={channel.id}>{channel.name}</option>
                        ))}
                      </select>
                    </div>

                    <div className="form-group">
                      <label>Customer Name *</label>
                      {showCustomerLov ? (
                        <div style={{ position: 'relative' }}>
                          <input
                            type="text"
                            value={customerInput}
                            onChange={handleCustomerInputChange}
                            onFocus={() => {
                              if (customerInput && filteredCustomers.length > 0 && !isReadOnly) {
                                setShowCustomerDropdown(true);
                              }
                            }}
                            onBlur={() => {
                              setTimeout(() => setShowCustomerDropdown(false), 200);
                            }}
                            placeholder="Enter Customer Name"
                            required
                            autoComplete="off"
                            disabled={isReadOnly}
                          />
                          {showCustomerDropdown && filteredCustomers.length > 0 && (
                            <div className="autocomplete-dropdown">
                              {filteredCustomers.map((customer) => (
                                <div
                                  key={customer.id}
                                  onClick={() => handleCustomerSelect(customer)}
                                  className="autocomplete-item"
                                >
                                  {customer.customer_name}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      ) : (
                        <input
                          type="text"
                          name="customer_name"
                          value={orderForm.customer_name}
                          onChange={handleOrderFormChange}
                          placeholder="Enter Customer Name"
                          required
                          disabled={isReadOnly}
                        />
                      )}
                    </div>

                    <div className="form-group">
                      <label>Order Date *</label>
                      <input
                        type="date"
                        name="order_date"
                        value={orderForm.order_date}
                        onChange={handleOrderFormChange}
                        required
                        disabled={isReadOnly}
                      />
                    </div>

                    <div className="form-group">
                      <label>Delivery Date</label>
                      <input
                        type="date"
                        name="delivery_date"
                        value={orderForm.delivery_date || ''}
                        onChange={handleOrderFormChange}
                        disabled={isReadOnly}
                      />
                    </div>

                    <div className="form-group">
                      <label>Delivery Window</label>
                      <input
                        type="text"
                        name="delivery_window"
                        value={orderForm.delivery_window || ''}
                        onChange={handleOrderFormChange}
                        placeholder="e.g. 09:00–11:00"
                        disabled={isReadOnly}
                      />
                    </div>

                    <div className="form-group">
                      <label>Order State *</label>
                      <select
                        name="order_state"
                        value={orderForm.order_state}
                        onChange={handleOrderFormChange}
                        required
                        disabled={isReadOnly}
                      >
                        {orderStates.map(state => (
                          <option key={state} value={state}>{state}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

              {/* Order Lines Section */}
              <div className="form-section">
                <div className="section-title-row">
                  <h4 className="section-title">Order Lines</h4>
                  {!isReadOnly && (
                    <button type="button" className="btn-add-line" onClick={addOrderLine}>
                      + Add Line
                    </button>
                  )}
                </div>

                <div className="order-lines-container">
                  {/* Header Row */}
                  <div className="order-line-header">
                    <div className="line-header-cell">Product *</div>
                    <div className="line-header-cell line-field-small">Quantity *</div>
                    <div className="line-header-cell line-field-small">Unit Price *</div>
                    <div className="line-header-cell line-field-small">Total</div>
                    <div className="line-header-cell line-actions"></div>
                  </div>

                  {/* Data Rows */}
                  {orderLines.map((line, index) => line.is_pack ? (
                    /* ── Pack line (always readonly) ── */
                    <div key={index} className="order-line-row order-line-row--pack">
                      <div className="line-field">
                        <input type="text" value={line.name || 'Pack'} readOnly className="readonly-field" />
                      </div>
                      <div className="line-field line-field-small">
                        <input type="number" value={line.qty} readOnly className="readonly-field" />
                      </div>
                      <div className="line-field line-field-small">
                        <input type="number" value={line.unit_price} readOnly className="readonly-field" />
                      </div>
                      <div className="line-field line-field-small">
                        <input type="number" value={line.total_price} readOnly className="readonly-field" />
                      </div>
                      <div className="line-actions">
                        <button
                          type="button"
                          className="btn-pack-detail"
                          onClick={() => setPackDetailLine(line)}
                          title="View pack contents"
                        >
                          Details
                        </button>
                      </div>
                    </div>
                  ) : (
                    /* ── Regular product line ── */
                    <div key={index} className="order-line-row">
                      <div className="line-field" style={{ position: 'relative' }}>
                        <input
                          type="text"
                          value={productInputs[index] || ''}
                          onChange={(e) => handleProductInputChange(index, e.target.value)}
                          onFocus={() => {
                            if (productInputs[index] && filteredProducts[index]?.length > 0 && !isReadOnly) {
                              setShowProductDropdown(prev => ({ ...prev, [index]: true }));
                            }
                          }}
                          onBlur={() => {
                            setTimeout(() => {
                              setShowProductDropdown(prev => ({ ...prev, [index]: false }));
                            }, 200);
                          }}
                          placeholder="Enter Product Name"
                          autoComplete="off"
                          disabled={isReadOnly}
                        />
                        {showProductDropdown[index] && filteredProducts[index]?.length > 0 && (
                          <div className="autocomplete-dropdown">
                            {filteredProducts[index].map((product) => (
                              <div
                                key={product.id}
                                onClick={() => handleProductSelect(index, product)}
                                className="autocomplete-item"
                              >
                                {product.name} ({product.uom})
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="line-field line-field-small">
                        <input
                          type="number"
                          value={line.qty}
                          onChange={(e) => handleLineChange(index, 'qty', e.target.value)}
                          placeholder="0"
                          step="0.01"
                          disabled={isReadOnly}
                        />
                      </div>
                      <div className="line-field line-field-small">
                        <input
                          type="number"
                          value={line.unit_price}
                          onChange={(e) => handleLineChange(index, 'unit_price', e.target.value)}
                          placeholder="0.00"
                          step="0.01"
                          disabled={isReadOnly}
                        />
                      </div>
                      <div className="line-field line-field-small">
                        <input
                          type="number"
                          value={line.total_price}
                          readOnly
                          className="readonly-field"
                        />
                      </div>
                      {!isReadOnly && (
                        <div className="line-actions">
                          <button
                            type="button"
                            className="btn-remove-line"
                            onClick={() => removeOrderLine(index)}
                            title="Remove Line"
                          >
                            ✕
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                <div className="order-total">
                  <strong>Order Total: ₹{orderLines.reduce((sum, line) => sum + (parseFloat(line.total_price) || 0), 0).toFixed(2)}</strong>
                </div>
              </div>
              </div>

              <div className="form-actions">
                <button type="button" className="btn-cancel" onClick={() => { setShowModal(false); setEditingOrder(null); setIsReadOnly(false); resetForm(); }}>
                  {isReadOnly ? 'Close' : 'Cancel'}
                </button>
                {!isReadOnly && (
                  <button type="submit" className="btn-submit">
                    {editingOrder ? 'Update Order' : 'Create Order'}
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Pack Detail Modal */}
      {packDetailLine && (
        <div className="modal-overlay" onClick={() => setPackDetailLine(null)}>
          <div className="pack-detail-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{packDetailLine.name} — Pack Contents</h3>
              <button className="close-button" onClick={() => setPackDetailLine(null)}>×</button>
            </div>
            <div style={{ padding: '0 1rem 1rem' }}>
              {packDetailLine.selectionGroups && packDetailLine.selectionGroups.length > 0 ? (
                packDetailLine.selectionGroups.map((g, gi) => (
                  <div key={gi} style={{ marginBottom: '0.75rem' }}>
                    <div className="pack-detail-group-label">{g.name}</div>
                    <table className="pack-detail-table">
                      <tbody>
                        {g.items.map((it, ii) => (
                          <tr key={ii}>
                            <td>{it.product_name}</td>
                            <td className="pack-detail-qty">×{it.qty}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ))
              ) : (
                <p style={{ color: '#aaa', fontSize: '0.875rem' }}>No selection details available.</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Receipt & Deductions Modal */}
      {showReceiptModal && selectedOrderForReceipt && (
        <div className="modal-overlay" onClick={handleCloseReceiptModal}>
          <div className="modal-content order-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Receipts & Deductions - Order #{selectedOrderForReceipt.order_no} {isReceiptReadOnly && <span style={{ color: '#666', fontSize: '0.9rem' }}>(View Only)</span>}</h3>
              <button className="close-button" onClick={handleCloseReceiptModal}>×</button>
            </div>

            <div className="modal-body" style={{ padding: '15px' }}>
              {error && <div className="error-message" style={{ margin: '0 0 8px 0' }}>{error}</div>}
              {successMessage && <div className="success-message" style={{ margin: '0 0 8px 0' }}>{successMessage}</div>}

              {/* Order Summary */}
              <div style={{
                background: '#f8f9fa',
                padding: '10px',
                borderRadius: '6px',
                marginBottom: '3px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <div>
                  <strong>{selectedOrderForReceipt.customer_name || 'N/A'}</strong>
                  <div style={{ fontSize: '0.85rem', color: '#666', marginTop: '3px' }}>
                    Total: ₹{selectedOrderForReceipt.receipt_amount || '0.00'} |
                    Paid: ₹{orderPaidTotal.total_amount || '0.00'}
                  </div>
                </div>
                {!isReceiptReadOnly && (
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      className="btn-submit"
                      onClick={handleRefreshPaidTotal}
                      style={{ padding: '6px 12px', fontSize: '0.85rem' }}
                    >
                      🔄 Refresh
                    </button>
                    {showFullyPaid && (
                      <button
                        className="btn-submit"
                        onClick={handleMarkFullyPaid}
                        style={{ padding: '6px 12px', background: '#28a745', fontSize: '0.85rem' }}
                      >
                        ✅ Mark Fully Paid
                      </button>
                    )}
                  </div>
                )}
              </div>

              {/* Receipt Form - Hide when read-only */}
              {!isReceiptReadOnly && (
                <form onSubmit={handleReceiptSubmit} style={{ marginBottom: '3px' }}>
                  <h4 style={{ marginBottom: '8px', fontSize: '1rem' }}>Add Receipt</h4>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    <select
                      name="receipt_id"
                      value={receiptForm.receipt_id}
                      onChange={handleReceiptFormChange}
                      required
                      style={{ flex: '2', minWidth: '200px', padding: '6px', borderRadius: '4px', border: '1px solid #ddd', fontSize: '0.85rem' }}
                    >
                      <option value="">Select Receipt</option>
                      {receiptNames.map(receipt => (
                        <option key={receipt.id} value={receipt.id}>
                          {receipt.name}
                        </option>
                      ))}
                    </select>
                    <input
                      type="date"
                      name="receipt_date"
                      value={receiptForm.receipt_date}
                      onChange={handleReceiptFormChange}
                      readOnly
                      style={{ flex: '1', minWidth: '120px', padding: '6px', borderRadius: '4px', border: '1px solid #ddd', background: '#f5f5f5', fontSize: '0.85rem' }}
                    />
                    <input
                      type="number"
                      name="balance_amount"
                      value={receiptForm.balance_amount}
                      onChange={handleReceiptFormChange}
                      placeholder="Balance"
                      readOnly
                      style={{ width: '90px', padding: '6px', borderRadius: '4px', border: '1px solid #ddd', background: '#f5f5f5', fontSize: '0.85rem' }}
                    />
                    <input
                      type="number"
                      name="receipt_amount"
                      value={receiptForm.receipt_amount}
                      onChange={handleReceiptFormChange}
                      placeholder="Amount"
                      required
                      step="0.01"
                      style={{ width: '110px', padding: '6px', borderRadius: '4px', border: '1px solid #ddd', fontSize: '0.85rem' }}
                    />
                    <button type="submit" className="btn-submit" style={{ padding: '6px 16px', fontSize: '0.85rem' }}>
                      Add Receipt
                    </button>
                  </div>
                </form>
              )}

              {/* Receipt Lines Table */}
              <h4 style={{ marginBottom: '8px', fontSize: '1rem' }}>Receipt Lines</h4>
              <div style={{ marginBottom: '3px', maxHeight: receiptLines.length === 0 ? '60px' : '150px', overflowY: 'auto' }}>
                <table className="orders-table" style={{ fontSize: '0.9rem' }}>
                  <thead>
                    <tr>
                      <th>Receipt</th>
                      <th>Type</th>
                      <th>Date</th>
                      <th>Amount</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {receiptLines.length === 0 ? (
                      <tr>
                        <td colSpan="5" className="no-data" style={{ padding: '8px' }}>No receipt lines found</td>
                      </tr>
                    ) : (
                      receiptLines.map((line, index) => {
                        const typeLabel = line.receipt_type_name || `Type ${line.receipt_type_id}`;
                        return (
                        <tr key={index}>
                          <td>{line.name}</td>
                          <td><span style={{ fontSize: '0.78rem', fontWeight: 600 }}>{typeLabel}</span></td>
                          <td>{line.receipt_date ? new Date(line.receipt_date).toLocaleDateString() : '-'}</td>
                          <td>₹{line.receipt_amount}</td>
                          <td>
                            {!isReceiptReadOnly && (
                              <button
                                className="btn-icon btn-delete"
                                onClick={() => handleDeleteReceiptLine(line)}
                                title="Delete Receipt"
                                style={{ fontSize: '1.2rem' }}
                              >
                                🗑️
                              </button>
                            )}
                          </td>
                        </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>

              {/* Deduction Form - Hide when read-only */}
              {!isReceiptReadOnly && (
                <form onSubmit={handleDeductionSubmit} style={{ marginBottom: '3px' }}>
                  <h4 style={{ marginBottom: '8px', fontSize: '1rem' }}>Add Deduction</h4>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '10px' }}>
                    <select
                      name="deduction_type"
                      value={deductionForm.deduction_type}
                      onChange={handleDeductionFormChange}
                      required
                      style={{ flex: '1', minWidth: '180px', padding: '6px', borderRadius: '4px', border: '1px solid #ddd', fontSize: '0.85rem' }}
                    >
                      <option value="">Select Deduction Type</option>
                      <option value="Commission">Commission</option>
                      <option value="TCS">TCS</option>
                      <option value="GST">GST</option>
                      <option value="Payment Gateway Charges">Payment Gateway Charges</option>
                      <option value="Adjustments">Adjustments</option>
                      <option value="CPC Ads">CPC Ads</option>
                    </select>
                    <input
                      type="number"
                      name="deduction_amount"
                      value={deductionForm.deduction_amount}
                      onChange={handleDeductionFormChange}
                      placeholder="Amount"
                      required
                      step="0.01"
                      style={{ width: '120px', padding: '6px', borderRadius: '4px', border: '1px solid #ddd', fontSize: '0.85rem' }}
                    />
                    <button type="submit" className="btn-submit" style={{ padding: '6px 16px', fontSize: '0.85rem' }}>
                      Add Deduction
                    </button>
                  </div>

                  {/* Auto-generate deductions */}
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                    <label style={{ margin: 0, fontSize: '0.85rem' }}>Commission%</label>
                    <input
                      type="number"
                      value={commissionPercent}
                      onChange={(e) => setCommissionPercent(Number(e.target.value))}
                      style={{ width: '70px', padding: '5px', borderRadius: '4px', border: '1px solid #ddd', fontSize: '0.85rem' }}
                    />
                    <label style={{ margin: 0, fontSize: '0.85rem' }}>TCS%</label>
                    <input
                      type="number"
                      value={tcsPercent}
                      onChange={(e) => setTcsPercent(Number(e.target.value))}
                      style={{ width: '70px', padding: '5px', borderRadius: '4px', border: '1px solid #ddd', fontSize: '0.85rem' }}
                    />
                    <label style={{ margin: 0, fontSize: '0.85rem' }}>GST%</label>
                    <input
                      type="number"
                      value={gstPercent}
                      onChange={(e) => setGstPercent(Number(e.target.value))}
                      style={{ width: '70px', padding: '5px', borderRadius: '4px', border: '1px solid #ddd', fontSize: '0.85rem' }}
                    />
                    <button
                      type="button"
                      className="btn-submit"
                      onClick={handleGenerateDeductions}
                      style={{ padding: '6px 12px', background: '#28a745', fontSize: '0.85rem' }}
                    >
                      🚀 Generate
                    </button>
                  </div>
                </form>
              )}

              {/* Deductions Table */}
              <h4 style={{ marginBottom: '8px', fontSize: '1rem' }}>Deductions</h4>
              <div style={{ maxHeight: orderDeductions.length === 0 ? '60px' : '150px', overflowY: 'auto' }}>
                <table className="orders-table" style={{ fontSize: '0.9rem' }}>
                  <thead>
                    <tr>
                      <th>Type</th>
                      <th>Expense Number</th>
                      <th>Amount</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orderDeductions.length === 0 ? (
                      <tr>
                        <td colSpan="4" className="no-data" style={{ padding: '8px' }}>No deductions found</td>
                      </tr>
                    ) : (
                      orderDeductions.map((deduction, index) => (
                        <tr key={index}>
                          <td>{deduction.deduction_type}</td>
                          <td>{deduction.expense_no || '-'}</td>
                          <td>₹{deduction.deduction_amount}</td>
                          <td>
                            {!isReceiptReadOnly && (
                              <button
                                className="btn-icon btn-delete"
                                onClick={() => handleDeleteDeduction(deduction)}
                                title="Delete Deduction"
                                style={{ fontSize: '1.2rem' }}
                              >
                                🗑️
                              </button>
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="form-actions">
              <button type="button" className="btn-cancel" onClick={handleCloseReceiptModal}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cancel & Refund Modal */}
      {cancelOrder && (
        <div className="modal-overlay" onClick={() => !cancelLoading && setCancelOrder(null)}>
          <div className="modal-content so-cancel-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Cancel Order</h3>
              <button className="close-button" onClick={() => !cancelLoading && setCancelOrder(null)}>×</button>
            </div>
            <div className="modal-body" style={{ padding: '20px 24px' }}>
              <p style={{ fontSize: '15px', color: '#222', margin: '0 0 12px' }}>
                Are you sure you want to cancel order <strong>{cancelOrder.order_no}</strong>?
              </p>
              <div style={{ fontSize: '14px', color: '#555', background: '#FFF3E0', borderRadius: '8px', padding: '10px 14px', margin: '0 0 8px' }}>
                <div style={{ marginBottom: '4px', fontWeight: 600 }}>The following refunds will be issued:</div>
                {parseFloat(cancelOrder.razorpay_amount || 0) > 0 && (
                  <div>• Razorpay refund: <strong>₹{parseFloat(cancelOrder.razorpay_amount).toFixed(2)}</strong></div>
                )}
                {parseFloat(cancelOrder.wallet_cash_amount || 0) > 0 && (
                  <div>• Wallet cash refund: <strong>₹{parseFloat(cancelOrder.wallet_cash_amount).toFixed(2)}</strong></div>
                )}
                {parseFloat(cancelOrder.wallet_points_amount || 0) > 0 && (
                  <div>• Reward points refund: <strong>{parseFloat(cancelOrder.wallet_points_amount).toFixed(2)} pts</strong></div>
                )}
                {!parseFloat(cancelOrder.razorpay_amount || 0) && !parseFloat(cancelOrder.wallet_cash_amount || 0) && !parseFloat(cancelOrder.wallet_points_amount || 0) && (
                  <div>A refund of <strong>₹{cancelOrder.order_amount}</strong> will be issued.</div>
                )}
              </div>
              {cancelError && (
                <p style={{ fontSize: '13px', color: '#C62828', background: '#FFEBEE', borderRadius: '6px', padding: '8px 12px', margin: '8px 0 0' }}>
                  {cancelError}
                </p>
              )}
            </div>
            <div className="form-actions">
              <button className="btn-cancel" onClick={() => setCancelOrder(null)} disabled={cancelLoading}>
                Back
              </button>
              <button className="btn-cancel-confirm" onClick={handleCancelConfirm} disabled={cancelLoading}>
                {cancelLoading ? 'Processing…' : 'Confirm Cancellation'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Assignment Modal */}
      {showAssignmentModal && selectedOrderForAssignment && (
        <div className="modal-overlay" onClick={handleCloseAssignmentModal}>
          <div className="modal-content order-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Assignments - Order #{selectedOrderForAssignment.order_no}</h3>
              <button className="close-button" onClick={handleCloseAssignmentModal}>×</button>
            </div>

            <div className="modal-body" style={{ padding: '15px' }}>
              {error && <div className="error-message" style={{ margin: '0 0 8px 0' }}>{error}</div>}
              {successMessage && <div className="success-message" style={{ margin: '0 0 8px 0' }}>{successMessage}</div>}

              {/* Order Summary */}
              <div style={{
                background: '#f8f9fa',
                padding: '10px',
                borderRadius: '6px',
                marginBottom: '3px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <div>
                  <strong>{selectedOrderForAssignment.customer_name || 'N/A'}</strong>
                  <div style={{ fontSize: '0.85rem', color: '#666', marginTop: '3px' }}>
                    Order Total: ₹{selectedOrderForAssignment.receipt_amount || '0.00'}
                  </div>
                </div>
              </div>

              {/* Assignment Form */}
              <form onSubmit={handleAssignmentSubmit} style={{ marginBottom: '3px' }}>
                <h4 style={{ marginBottom: '8px', fontSize: '1rem' }}>Add Assignment</h4>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  <select
                    name="user_id"
                    value={assignmentForm.user_id}
                    onChange={handleAssignmentFormChange}
                    required
                    style={{ flex: '2', minWidth: '200px', padding: '6px', borderRadius: '4px', border: '1px solid #ddd', fontSize: '0.85rem' }}
                  >
                    <option value="">Select User</option>
                    {users.map(u => (
                      <option key={u.id} value={u.id}>
                        {u.name}
                      </option>
                    ))}
                  </select>
                  <select
                    name="order_role"
                    value={assignmentForm.order_role}
                    onChange={handleAssignmentFormChange}
                    required
                    style={{ flex: '1', minWidth: '150px', padding: '6px', borderRadius: '4px', border: '1px solid #ddd', fontSize: '0.85rem' }}
                  >
                    <option value="">Select Role</option>
                    <option value="OWNER">Owner</option>
                    <option value="DISPATCH">Dispatch</option>
                    <option value="FULFILL">Fulfill</option>
                  </select>
                  <button type="submit" className="btn-submit" style={{ padding: '6px 16px', fontSize: '0.85rem' }}>
                    Add Assignment
                  </button>
                </div>
              </form>

              {/* Assignments Table */}
              <h4 style={{ marginBottom: '8px', fontSize: '1rem' }}>Current Assignments</h4>
              <div style={{ maxHeight: assignments.length === 0 ? '60px' : '150px', overflowY: 'auto' }}>
                <table className="orders-table" style={{ fontSize: '0.9rem' }}>
                  <thead>
                    <tr>
                      <th>User</th>
                      <th>Role</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {assignments.length === 0 ? (
                      <tr>
                        <td colSpan="3" className="no-data" style={{ padding: '8px' }}>No assignments found</td>
                      </tr>
                    ) : (
                      assignments.map((assignment, index) => (
                        <tr key={index}>
                          <td>{assignment.user_name}</td>
                          <td>{assignment.order_role_name}</td>
                          <td>
                            <button
                              className="btn-icon btn-delete"
                              onClick={() => handleDeleteAssignment(assignment)}
                              title="Delete Assignment"
                              style={{ fontSize: '1.2rem' }}
                            >
                              🗑️
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="form-actions">
              <button type="button" className="btn-cancel" onClick={handleCloseAssignmentModal}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SalesOrders;
